import type { AppRole } from '@empower-safety/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export type InvitationStatus = 'pending' | 'accepted' | 'revoked' | 'expired';

export interface InvitationDto {
  id: string;
  organisation_id: string;
  email: string;
  phone_e164: string | null;
  app_role: AppRole;
  team_ids: string[];
  clerk_invitation_id: string | null;
  source: string;
  source_provider: string | null;
  source_employee_id: string | null;
  status: InvitationStatus;
  metadata: Record<string, unknown>;
  invited_by: string | null;
  created_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
}

export interface InviteItem {
  email: string;
  phone_e164?: string;
  app_role?: AppRole;
  team_ids?: string[];
  metadata?: Record<string, unknown>;
}

export type CreateInvitationOutcome =
  | { status: 'created'; invitation: InvitationDto }
  | { status: 'already_member'; email: string }
  | { status: 'already_pending'; invitation: InvitationDto }
  | { status: 'failed'; email: string; reason: string };

export function usePendingInvitations() {
  const api = useApi();
  return useQuery({
    queryKey: ['invitations', 'pending'],
    queryFn: () => api<{ invitations: InvitationDto[] }>('/invitations?status=pending&limit=200'),
    staleTime: 15_000,
  });
}

export function useCreateInvitations() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { invitations: InviteItem[]; redirect_url?: string }) =>
      api<{ outcomes: CreateInvitationOutcome[] }>('/invitations', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invitations'] });
      qc.invalidateQueries({ queryKey: ['roster'] });
    },
  });
}

export function useRevokeInvitation() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ invitation: InvitationDto }>(`/invitations/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}

// Resend a pending invitation. Revokes the prior Clerk invitation (best-effort)
// and creates a fresh one, so the recipient gets a new email. Used when the
// original Clerk-side invite never reached the recipient.
//
// The `redirect_url` is the URL Clerk uses for the invitation accept link in
// the email. Always pass the dashboard's origin so the link lands on a
// Clerk-enabled page that can consume the __clerk_ticket; without it Clerk
// falls back to its app-level config which may not be set.
export function useResendInvitation() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; redirect_url?: string }) =>
      api<{ invitation: InvitationDto }>(`/invitations/${input.id}/resend`, {
        method: 'POST',
        body: JSON.stringify({ redirect_url: input.redirect_url }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}
